import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { useUserRole } from '@/hooks/useUserRole';
import { useToast } from '@/hooks/use-toast';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireRole?: 'super_user';
  redirectTo?: string;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = true,
  requireRole,
  redirectTo = '/auth',
}) => {
  const { session, loading } = useAuth();
  const { userRole, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const isLoading = loading || (requireRole && roleLoading);

  useEffect(() => {
    if (!isLoading) {
      if (requireAuth && !session) {
        toast({
          title: 'Authentication required',
          description: 'Please sign in to access this page.',
        });
        navigate(redirectTo, {
          state: { from: location.pathname },
          replace: true,
        });
      } else if (requireRole && session && userRole !== requireRole) {
        toast({
          title: 'Access denied',
          description: 'You do not have permission to access this page.',
        });
        navigate('/', { replace: true });
      } else if (!requireAuth && session && location.pathname === '/auth') {
        // Redirect authenticated users away from auth page
        const from = location.state?.from || '/';
        navigate(from, { replace: true });
      }
    }
  }, [
    session,
    isLoading,
    requireAuth,
    requireRole,
    userRole,
    navigate,
    redirectTo,
    location,
    toast,
  ]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[var(--background)] text-[var(--foreground)]">
        <LoadingSpinner size="lg" />
        <p className="text-muted-foreground text-sm">Verifying your session...</p>
      </div>
    );
  }

  if (requireAuth && !session) {
    return null; // Will redirect in useEffect
  }

  if (requireRole && userRole !== requireRole) {
    return null; // Will redirect in useEffect
  }

  return <>{children}</>;
};
